import React, { useState } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';

const ComingSoon = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setStatus('Please enter a valid email address.');
      return;
    }

    // Start submission process
    setIsSubmitting(true);
    setStatus('');

    try {
      // Replace these with your actual EmailJS credentials
      const result = await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,     // EmailJS Service ID
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,    // EmailJS Teplate ID
        {
          from_email: email,
          to_email: email,                           // This might not be working
          email: email,                              // Try this parameter name instead
          to: email,                                 // Or try this parameter name
          recipient: email,                          // Or try this parameter name
          title: "GalaxyModz Portfolio Updates",     
          message: 'Interested in galaxymodz portfolio updates'
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY      // EmailJS Public Key
      );

      // Success
      setStatus('Thank you! You\'ll be notified when the portfolio is ready.');
      setEmail('');
    } catch (error) {
      // Error handling
      console.error('Email send error:', error);
      setStatus('Failed to submit. Please try again later.');
    } finally {
      // Always reset submission state
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a1a] text-cyan-200 flex items-center justify-center overflow-hidden relative">
      {/* Background Grid */}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        {[...Array(20)].map((_, i) => (
          <React.Fragment key={i}>
            <div 
              className="absolute top-0 bottom-0 w-px bg-cyan-500"
              style={{ left: `${5 * i}%`, opacity: 0.1 }}
            />
            <div 
              className="absolute left-0 right-0 h-px bg-purple-500"
              style={{ top: `${5 * i}%`, opacity: 0.1 }}
            />
          </React.Fragment>
        ))}
      </div>

      {/* Neon Border Effect */}
      <div className="fixed inset-[10px] border-4 border-transparent border-t-cyan-500 border-r-cyan-500 animate-spin-slow opacity-50 pointer-events-none"></div>
      <div className="fixed inset-[20px] border-4 border-transparent border-b-purple-500 border-l-purple-500 animate-spin-reverse-slow opacity-50 pointer-events-none"></div>

      {/* Main Content */}
      <motion.div 
        className="text-center z-10 p-8 w-full max-w-xl"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-5xl md:text-7xl font-bold mb-4 glitch-text" data-text="galaxymodz">
          galaxymodz
        </h1>
        <p className="text-xl md:text-2xl mb-8 neon-text">
          Portfolio Coming Soon
        </p>

        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ 
            delay: 0.3,
            type: "spring",
            stiffness: 300
          }}
        >
          <form onSubmit={handleSubmit} className="max-w-md mx-auto">
            <div className="flex items-center border-2 border-cyan-500 rounded-full overflow-hidden">
              <input 
                type="email" 
                placeholder="Enter your email for updates" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-transparent text-cyan-200 placeholder-cyan-500 focus:outline-none"
                required
              />
              <button 
                type="submit" 
                disabled={isSubmitting}
                className={`
                  bg-cyan-500 text-[#0a0a1a] px-6 py-3 
                  hover:bg-cyan-400 transition-colors
                  ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                {isSubmitting ? 'Sending...' : 'Notify Me'}
              </button>
            </div>
          </form>
          
          {/* Status Message */}
          {status && (
            <motion.p
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`
                mt-4 text-sm 
                ${status.includes('Thank you') 
                  ? 'text-green-400' 
                  : 'text-red-400'
                }
              `}
            >
              {status}
            </motion.p>
          )}
        </motion.div>

        <div className="mt-12 space-x-4">
          <motion.a 
            href="https://github.com/BetaDevlopment" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block bg-purple-700 text-white px-6 py-3 rounded-full hover:bg-purple-600 transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            GitHub
          </motion.a>
          <motion.a 
            href="https://discord.com/users/455734511741960205" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block bg-cyan-700 text-white px-6 py-3 rounded-full hover:bg-cyan-600 transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            Discord
          </motion.a>
        </div>
      </motion.div>

      {/* Floating Particles */}
      {[...Array(50)].map((_, i) => (
        <motion.div
          key={`particle-${i}`}
          className="absolute w-1 h-1 rounded-full bg-cyan-500"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            opacity: Math.random() * 0.5
          }}
          animate={{
            x: [0, Math.random() * 20 - 10, 0],
            y: [0, Math.random() * 20 - 10, 0],
            scale: [1, 1.5, 1]
          }}
          transition={{
            duration: Math.random() * 5 + 3,
            repeat: Infinity,
            repeatType: "loop"
          }}
        />
      ))}
    </div>
  );
};

export default ComingSoon;
